import { API } from '@aws-amplify/api';

import { logger } from './logger';

import packageMeta from '@root/package.json';

interface MakeRequestOptions {
  method: 'get' | 'post' | 'put' | 'del';
  url: string;
  data?: any;
}

interface RequestErrorConstructorOptions {
  message?: string;
  code?: string;
}

export class RequestError extends Error {
  code: string | null;

  constructor(options: string | RequestErrorConstructorOptions) {
    let message: string | null = null;
    let code: string | null = null;

    if (typeof options === 'string') {
      message = options;
    } else if (options && typeof options === 'object') {
      message = options.message || 'An unknown error occurred.';
      code = options.code || 'UNKNOWN_ERROR';
    }

    super(message as string);

    this.code = code;
  }
}

class Request {
  /**
   * Make a web request.
   *
   * @param config Request configuration object.
   * @return A `Promise` that will resolve to the request's response.
   */
  async make(config: MakeRequestOptions) {
    let response: unknown = null;
    let error: unknown = null;

    const fn = API[config.method].bind(API) as
      | (typeof API)['get']
      | (typeof API)['post']
      | (typeof API)['put']
      | (typeof API)['del'];

    const options: Record<string, unknown> = { response: true };

    if (config.data) {
      options.body = config.data;
    }

    const headers = (options.headers ?? {}) as Record<string, string>;

    headers['x-gears-web-version'] = packageMeta.version;
    headers['x-gears-page'] = window.location.pathname;

    options.headers = headers;
    
    // const options = Object.assign(
    //   { response: true },
    //   config.data ? { body: config.data } : {}
    // );

    try {
      response = await fn('gearsAPI', config.url, options);
    } catch (err) {
      error = err;
    }

    response = response || error?.response || null;

    logger(response);

    let errorData: RequestErrorConstructorOptions | null = null;

    if (error) {
      errorData = error.response?.data || error;
    } else {
      errorData = response?.data?.error;
    }

    if (errorData) {
      throw new RequestError(errorData);
    }

    return response?.data?.data || response?.data || response;
  }

  /**
   * Make a `GET` request.
   *
   * @param url URL to use for request.
   * @return Promise that will resolve to the data, if any, received in the`
   * response.
   */
  get(url: string) {
    return this.make({ method: 'get', url });
  }

  /**
   * Make a `POST` request.
   *
   * @param url URL to use for request.
   * @param data Request data.
   * @return Promise that will resolve to the data, if any, received in the`
   * response.
   */
  post(url: string, data?: unknown) {
    return this.make({ method: 'post', url, data });
  }

  /**
   * Make a `PUT` request.
   *
   * @param url URL to use for request.
   * @param data Request data.
   * @return Promise that will resolve to the data, if any, received in the`
   * response.
   */
  put(url: string, data?: unknown) {
    return this.make({ method: 'put', url, data });
  }

  /**
   * Make a `DELETE` request.
   *
   * @param url URL to use for request.
   * @return Promise that will resolve to the data, if any, received in the`
   * response.
   */
  delete(url: string, config?: any) {
    return this.make({ method: 'del', url });
  }
}

export const request = new Request();
